import Notification from './Notification.js';
import { PostNotification, PostNotificationOptions } from './PostNotification.js';
import BrazeContentCardNotification from './BrazeContentCardNotification.js';
import VideoNotification from './VideoNotification.js';

export default class NotificationPanel {
    constructor() {
        this.nonEmptyTabNames = [];
        this.container = null;
        this.notifications = {
            services: [],
            updates: [],
            offers: [],
            reports: [],
            video: [],
            all: [],
        };
        this.enabledTabs = (window.foolNotificationBell.tabs ?? []).filter(tabName => Object.keys(this.notifications).includes(tabName));
    }

    openPanel() {
        const dialog = this.container.querySelector('dialog');
        const bellPosition = this.container.querySelector('.open-btn').getBoundingClientRect();

        dialog.style.right = `calc(100vw - ${ parseInt(bellPosition.right, 10) }px)`;
        dialog.style.top = `${ parseInt(bellPosition.bottom, 10) + 20 }px`;

        dialog.showModal();
    }

    initVisibilityToggle() {
        this.container.querySelector('.open-btn').addEventListener('click', () => this.openPanel());
        this.container.querySelector('.close-btn').addEventListener('click', () => {
            this.container.querySelector('dialog').close();
        });
    }

    initTabs() {
        const activateTab = (tabName) => {
            this.container.querySelectorAll('.tmf-tab').forEach(btn => {
                btn.classList.toggle('active', btn.dataset.tab === tabName);
            });
            this.container.querySelectorAll('.tmf-notification-content__tab').forEach(pane => {
                pane.classList.toggle('active', pane.dataset.tab === tabName);
            });
        };

        const initialTab = this.container.querySelector('.tmf-tab.active')?.dataset.tab ?? 'all';
        activateTab(initialTab);

        this.container.querySelector('.tmf-notification-tabs').addEventListener('click', (e) => {
            const btn = e.target.closest('.tmf-tab');
            if (btn) {
                activateTab(btn.dataset.tab);
            }
        });
    }

    setFavicon(href) {
        let favicon = document.querySelector('link[rel~="icon"]');
        if (favicon) {
            favicon.href = href;
        } else {
            document.head.insertAdjacentHTML('afterbegin', `<link rel="icon" href="${ href }">`);
        }
    }

    setAllRead() {
        this.notifications.all.forEach(n => Notification.markAsRead(n.id));
        this.container.querySelectorAll('[data-read-status="unread"]').forEach(tile => {
            tile.dataset.readStatus = 'read';
        });
        this.setFavicon('/favicon.ico');
        this.container.classList.remove('has-unread');
        this.container.querySelector('#tmf-mark-all-read').disabled = true;
    }

    initMarkAllRead() {
        const btn = this.container.querySelector('#tmf-mark-all-read');
        btn.disabled = true;
        btn.addEventListener('click', () => this.setAllRead());
    }

    initReadTracking() {
        this.container.addEventListener('click', (e) => {
            if (e.target.nodeName !== 'A') {
                return;
            }
            const tile = e.target.closest('.tmf-notification-tile');
            Notification.markAsRead(tile.dataset.notificationId);
            tile.dataset.readStatus = 'read';

            const hasUnread = !!this.container.querySelector('[data-read-status="unread"]');
            if (!hasUnread) {
                this.setFavicon('/favicon.ico');
                this.container.classList.remove('has-unread');
                this.container.querySelector('#tmf-mark-all-read').disabled = true;
            }
        });
    }

    renderTiles() {
        Object.keys(this.notifications).forEach(tabName => {
            const tab = this.container.querySelector(`.tmf-notification-content__tab[data-tab="${ tabName }"]`);
            this.notifications[tabName].forEach(notification => {
                tab.insertAdjacentHTML('beforeend', notification.renderTile());
            });
        });
    }

    gatherAndSortNotifications() {
        const byDateDesc = (a, b) => b.date - a.date;
        this.nonEmptyTabNames.forEach(tabName => this.notifications[tabName].sort(byDateDesc));
        this.notifications.all = this.nonEmptyTabNames.reduce((allItems, tabName) => allItems.concat(this.notifications[tabName]), []).sort(byDateDesc);
    }

    handleNotificationsChange() {
        if (this.nonEmptyTabNames.length) {
            this.container.querySelector('.tmf-notification-empty-state').style.display = 'none';
            this.gatherAndSortNotifications();
            this.renderTiles();

            if (this.nonEmptyTabNames.length > 1) {
                this.nonEmptyTabNames.forEach(tabName => this.container.querySelector(`.tmf-tab[data-tab="${ tabName }"]`).style.display = '');
            }

            const hasUnread = this.notifications.all.some(n => n.isUnread);
            this.container.querySelector('#tmf-mark-all-read').disabled = !hasUnread;
            if (hasUnread) {
                this.setFavicon('/favicon-notification.png');
                this.container.classList.add('has-unread');
            }
        }
    }

    movePanelIntoDialog() {
        const dialog = this.container.querySelector('dialog');
        dialog.appendChild(dialog.nextElementSibling);
    }

    init() {
        this.container = document.querySelector('.site-header .notification-bell');
        if (this.container && this.enabledTabs.length) {
            this.movePanelIntoDialog();
            this.initVisibilityToggle();
            this.initTabs();
            this.initMarkAllRead();
            this.initReadTracking();
            window.addEventListener('load', async () => {
                if (this.enabledTabs.includes('services')) {
                    const [posts, recommendations] = await Promise.all([
                        PostNotification.fetch(new PostNotificationOptions()),
                        PostNotification.fetch(new PostNotificationOptions().setPostTypeEndpoint('recommendation').setCallToActionLabel('Read Recommendation')),
                    ]);
                    this.notifications.services.push(...posts, ...recommendations);
                }
                if (this.enabledTabs.includes('reports')) {
                    const reportsOptions = new PostNotificationOptions()
                        .setPostTypeEndpoint('premiumreport')
                        .setCallToActionLabel('Read Report')
                        .setMinimumPublishDate('')
                        .enableRecentPurchaseFilter()
                        .disableAccessibleServicesFilter();
                    const reports = await PostNotification.fetch(reportsOptions);
                    this.notifications.reports.push(...reports);
                }
                if (this.enabledTabs.includes('updates')) {
                    this.notifications.updates.push(...BrazeContentCardNotification.fetch('updates'));
                }
                if (this.enabledTabs.includes('offers')) {
                    this.notifications.offers.push(...BrazeContentCardNotification.fetch('offers'));
                }
                if (this.enabledTabs.includes('video')) {
                    this.notifications.video.push(...await VideoNotification.fetch());
                }
                this.nonEmptyTabNames = Object.keys(this.notifications).filter(tabName => (tabName !== 'all' && this.notifications[tabName].length));
                this.handleNotificationsChange();
                console.log('NotificationPanel', this.notifications);
            });
        }
    }
}
